<script setup lang="ts">
import { NTabPane, NTabs } from "naive-ui";
import { computed, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import {
    getOrderedSteps,
    isOnboardingEnabled,
    isOnboardingInProgress,
} from "../constants/onboardingProgress";
import { CallToActionType } from "../models/onboarding_steps.ts";
import { queryRef } from "../router/helpers.ts";
import BankAccounts from "./ConfigTabs/BankAccounts.vue";
import Credentials from "./ConfigTabs/Credentials.vue";
import Properties from "./ConfigTabs/Properties.vue";
import Team from "./ConfigTabs/Team.vue";
import Vendors from "./ConfigTabs/Vendors.vue";
import OnboardingStepBanner from "../components/onboarding/OnboardingStepBanner.vue";

const tab = queryRef("config-tab", "team");
const route = useRoute();
const router = useRouter();

const propertyQueryParams = [
    "properties-search",
    "properties-proper-scope",
    "properties-groups",
    "properties-types",
    "property-notes-and-activity",
    "property-drawer-tab",
];
const bankAccountQueryParams = [
    "bank-accounts-search",
    "bank-accounts-proper-scope",
    "bank-accounts-corporate-flag",
    "bank-accounts-bank-names",
    "bank-accounts-property-ids",
    "bank-account-notes-and-activity",
    "bank-account-drawer-tab",
];

const orderedSteps = computed(() => getOrderedSteps());

function isTabDisabled(tabName: string): boolean {
    if (!isOnboardingInProgress()) return false;

    return tabName !== tab.value;
}

const bannerStep = computed(() => {
    if (route.path !== "/config") {
        return null;
    }

    const currentTab = route.query["config-tab"] as string | undefined;

    return orderedSteps.value.find((step) => {
        if (step.main_call_to_action_type !== CallToActionType.Path) {
            return false;
        }

        const stepPath = new URL(step.main_call_to_action_string as string, window.location.origin);
        return (
            stepPath.pathname === "/config" &&
            stepPath.searchParams.get("config-tab") === currentTab
        );
    });
});

const isOnboardingBannerVisible = computed(() => isOnboardingEnabled() && !!bannerStep.value);

function onBannerStepDone() {
    router.push("/onboarding");
}


watch(
    () => tab.value,
    (nextTab) => {
        const nextQuery = { ...route.query, "config-tab": nextTab };
        if (nextTab !== "properties") {
            for (const param of propertyQueryParams) {
                delete (nextQuery as any)[param];
            }
        }
        if (nextTab !== "bank-accounts") {
            for (const param of bankAccountQueryParams) {
                delete (nextQuery as any)[param];
            }
        }
        router.replace({ query: nextQuery });
    },
);
</script>

<template>
    <main>
        <onboarding-step-banner
            v-if="isOnboardingBannerVisible && bannerStep"
            :step="bannerStep"
            @completed="onBannerStepDone"
            @skipped="onBannerStepDone"
        />
        <section class="flex flex-col mt-8 mx-2 sm:mx-4 lg:mx-10">
            <h1 class="text-2xl font-semibold mb-6">Configuration</h1>
            <n-tabs type="line" animated v-model:value="tab">
                <n-tab-pane name="team" tab="Team" :disabled="isTabDisabled('team')">
                    <team />
                </n-tab-pane>
                <n-tab-pane
                    name="properties"
                    tab="Properties"
                    :disabled="isTabDisabled('properties')"
                >
                    <properties
                        :show-bulk-scope="
                            isOnboardingBannerVisible && bannerStep?.step_code === 'PROPERTIES'
                        "
                    />
                </n-tab-pane>
                <n-tab-pane
                    name="bank-accounts"
                    tab="Bank Accounts"
                    :disabled="isTabDisabled('bank-accounts')"
                >
                    <bank-accounts
                        :show-bulk-scope="
                            isOnboardingBannerVisible && bannerStep?.step_code === 'BANK_ACCOUNTS'
                        "
                    />
                </n-tab-pane>
                <n-tab-pane name="vendors" tab="Vendors" :disabled="isTabDisabled('vendors')">
                    <vendors />
                </n-tab-pane>
                <n-tab-pane
                    name="credentials"
                    tab="Credentials"
                    :disabled="isTabDisabled('credentials')"
                >
                    <credentials
                        :auto-complete-onboarding="
                            isOnboardingBannerVisible && bannerStep?.step_code === 'CREDENTIALS'
                        "
                    />
                </n-tab-pane>
            </n-tabs>
        </section>
    </main>
</template>
